import { useCallback, useEffect, useState } from 'react';
import { getTermsStatus, type TermsStatus } from '../services/profile';

export type GateStatus = 'loading' | 'needed' | 'done';

/**
 * Décide si l'étape post-auth OBLIGATOIRE d'acceptation doit s'afficher : une fois connecté, on
 * compare le consentement enregistré à la version courante du bundle CGU + confidentialité.
 * `reason` distingue une première acceptation ('never') d'une re-sollicitation après révision
 * ('outdated'), l'écran n'ayant ni le même texte ni les mêmes issues.
 * `dismiss` est appelé APRÈS acceptation (pas de skip). Erreur de lecture → 'done' (on n'enferme pas
 * l'utilisateur hors de l'app sur une lecture transitoire).
 */
export function useTermsGate(hasSession: boolean) {
  const [status, setStatus] = useState<GateStatus>('loading');
  const [reason, setReason] = useState<Exclude<TermsStatus, 'accepted'> | null>(null);

  useEffect(() => {
    if (!hasSession) {
      setStatus('loading');
      return;
    }
    let active = true;
    getTermsStatus()
      .then((s) => {
        if (!active) return;
        setReason(s === 'accepted' ? null : s);
        setStatus(s === 'accepted' ? 'done' : 'needed');
      })
      .catch(() => active && setStatus('done'));
    return () => {
      active = false;
    };
  }, [hasSession]);

  const dismiss = useCallback(() => setStatus('done'), []);

  return { status, reason, dismiss };
}
